import React from 'react';

import { Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  EmptyBoxIconWrapper,
  EmptyBoxWrapper,
  EmptyTextWrapper,
  HeaderLink,
  HeaderTextWrapper,
  IconWrapper,
} from 'components/features/components/recent-items/styled';
import MenuSectionHeading from 'components/menu-section-heading';
import useEffectOnce from 'hooks/use-effect-once';
import { useOfferHeader } from 'hooks/use-offer-header';
import { Section } from 'pages/home/styled';
import { useLocale } from 'state/intl';
import { REGIONS } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOffersContext } from 'state/offers';
import { routes } from 'utils/routing';

import Offers from './offers';

const StyledOffersSection = styled(Section)`
  width: auto;
  padding: 0 0.75rem;

  ${Styles.desktop`
    width: calc(100% - 2rem);
    padding: unset;
  `};
`;

const OffersSection = () => {
  const offerHeader = useOfferHeader();
  const { loading, offers: filteredOffers, refreshOffers, error } = useOffersContext();
  const showOffersOnHomepage = useFlag(LaunchDarklyFlag.ENABLE_OLD_OFFERS_HOMEPAGE_SECTION);
  const enableThOffersPage = useFlag(LaunchDarklyFlag.ENABLE_TH_OFFERS_PAGE);
  const { formatMessage } = useIntl();
  const { region: currentRegion, readableRegions } = useLocale();

  /**
   * Reload offers/coupons if OffersContext isn't loading that right now
   */
  useEffectOnce(() => {
    if (!loading && showOffersOnHomepage && !filteredOffers.length) {
      refreshOffers();
    }
  });

  // fail fast
  if (!showOffersOnHomepage) {
    return null;
  }

  if (error) {
    const regionError = error.graphQLErrors?.find(
      gqlError => gqlError.extensions?.code === 'LOYALTY_NOT_AVAILABLE_IN_USER_REGION'
    );

    if (regionError) {
      const { loyaltyRegion } = regionError.extensions as { loyaltyRegion: REGIONS };
      return (
        <EmptyBoxWrapper>
          <EmptyBoxIconWrapper>
            <Icon icon="info" color="primary" width="20px" aria-hidden />
          </EmptyBoxIconWrapper>
          <EmptyTextWrapper>
            {formatMessage({ id: 'offersUpdateYourRegion' })}
            <br />
            {formatMessage(
              { id: 'offersUpdateYourRegionSubtitle' },
              {
                currentRegion: readableRegions[currentRegion],
                barcodeRegion: readableRegions[loyaltyRegion],
              }
            )}
          </EmptyTextWrapper>
        </EmptyBoxWrapper>
      );
    }
  }

  const haveOffers = filteredOffers.length > 0;

  return (
    <StyledOffersSection data-testid="offers-homepage-section">
      <MenuSectionHeading as="h2">
        <HeaderTextWrapper>
          {offerHeader}
          {enableThOffersPage && haveOffers && (
            <HeaderLink to={routes.offers} data-testid="offers-page-link">
              {formatMessage({ id: 'viewAll' })}
              <IconWrapper>
                <Icon icon="next" color="primary" width="24px" aria-hidden />
              </IconWrapper>
            </HeaderLink>
          )}
        </HeaderTextWrapper>
      </MenuSectionHeading>
      {haveOffers ? (
        <Offers />
      ) : (
        <EmptyBoxWrapper>
          <EmptyBoxIconWrapper>
            <Icon icon="info" color="primary" width="20px" aria-hidden />
          </EmptyBoxIconWrapper>
          <EmptyTextWrapper>
            {formatMessage({ id: 'offersAreCurrentlyNotAvailable' })}
            <br />
            {formatMessage({ id: 'pleaseCheckBackNextWeek' })}
          </EmptyTextWrapper>
        </EmptyBoxWrapper>
      )}
    </StyledOffersSection>
  );
};

export default OffersSection;
